import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, CheckCircle, AlertTriangle, XCircle, 
  MapPin, Calendar, Thermometer, Package, Award,
  Truck, Clock, Search, QrCode, RefreshCw, Download
} from 'lucide-react';
import { getLotByCode, isMockProvider } from '../lib/traceabilite/provider';
import { generateTraceabilityCertificate } from '../lib/traceabilite/pdfGenerator';

const LotDetails = () => {
  const { code } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = language === 'fr';

  const [lot, setLot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCode, setSearchCode] = useState('');

  useEffect(() => {
    const fetchLot = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getLotByCode(code);
        if (data) {
          setLot(data);
        } else {
          setError('not_found');
        }
      } catch (err) {
        console.error('Error fetching lot:', err);
        setError('error');
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      fetchLot();
    }
  }, [code]);

  const handleNewSearch = (e) => {
    e.preventDefault();
    if (searchCode.trim()) {
      navigate(`/tracabilite/${searchCode.trim().toUpperCase()}`);
      setSearchCode('');
    }
  };

  const handleDownloadPDF = () => {
    if (lot) {
      generateTraceabilityCertificate(lot, language);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F2E8] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#1A5C38] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t ? 'Recherche en cours...' : 'Searching...'}</p>
        </div>
      </div>
    );
  }

  // Error / Not found state
  if (error || !lot) {
    return (
      <div className="min-h-screen bg-[#F8F2E8]">
        <div className="container py-20">
          <Link 
            to="/tracabilite" 
            className="inline-flex items-center gap-2 text-[#1A5C38] hover:underline mb-8"
          >
            <ArrowLeft size={20} />
            {t ? 'Retour à la recherche' : 'Back to search'}
          </Link>

          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="text-gray-400" size={40} />
            </div>
            <h1 className="text-3xl font-bold text-[#1A5C38] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t ? 'Lot non trouvé' : 'Lot not found'}
            </h1>
            <p className="text-gray-600 mb-2">
              {t 
                ? `Le code "${code}" ne correspond à aucun lot enregistré dans notre système.`
                : `The code "${code}" does not match any lot registered in our system.`}
            </p>
            <p className="text-gray-500 text-sm mb-8">
              {t 
                ? 'Vérifiez que le code est correctement orthographié ou scannez à nouveau le QR code.'
                : 'Check that the code is spelled correctly or scan the QR code again.'}
            </p>

            {/* New search */}
            <form onSubmit={handleNewSearch} className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                  placeholder={t ? 'Essayer un autre code...' : 'Try another code...'}
                  className="w-full px-6 py-4 pr-14 rounded-full border-2 border-[#1A5C38]/20 focus:border-[#1A5C38] focus:outline-none transition-colors"
                  data-testid="lot-retry-search-input"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#1A5C38] hover:bg-[#145230] rounded-full flex items-center justify-center transition-colors"
                  data-testid="lot-retry-search-button"
                >
                  <Search className="text-white" size={18} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Status configuration
  const statusConfig = {
    verified: {
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-500',
      label: t ? 'Vérifié' : 'Verified'
    },
    recalled: {
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-500',
      label: t ? 'Rappelé' : 'Recalled'
    },
    pending: {
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      borderColor: 'border-yellow-500',
      label: t ? 'En cours' : 'Pending'
    }
  };

  const status = statusConfig[lot.status] || statusConfig.verified;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-[#F8F2E8] dark:bg-[#0F1A14] transition-colors">
      {/* Header */}
      <div className={`py-6 ${lot.status === 'recalled' ? 'bg-red-600' : 'bg-[#1A5C38] dark:bg-[#1A5C38]'}`}>
        <div className="container">
          <Link 
            to="/tracabilite" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft size={20} />
            {t ? 'Nouvelle recherche' : 'New search'}
          </Link>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${status.bgColor}`}>
                <StatusIcon className={status.color} size={28} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                    {lot.product.name[language] || lot.product.name.fr}
                  </h1>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.bgColor} ${status.color}`}>
                    {status.label}
                  </span>
                </div>
                <p className="text-white/70 flex items-center gap-2 mt-1">
                  <QrCode size={16} />
                  {lot.code}
                </p>
              </div>
            </div>
            
            {/* PDF Download Button */}
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl transition-colors border border-white/20"
              data-testid="download-pdf-button"
            >
              <Download size={18} />
              <span className="hidden sm:inline">{t ? 'Télécharger PDF' : 'Download PDF'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Recall Alert */}
      {lot.status === 'recalled' && lot.recallReason && (
        <div className="bg-red-50 border-b-4 border-red-500 py-6">
          <div className="container">
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-red-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="text-lg font-bold text-red-700 mb-2">
                  {t ? 'Produit rappelé' : 'Product recalled'}
                </h2>
                <p className="text-red-600">
                  {lot.recallReason[language] || lot.recallReason.fr}
                </p>
                {lot.recallDate && (
                  <p className="text-red-500 text-sm mt-2">
                    {t ? 'Date du rappel : ' : 'Recall date: '}{lot.recallDate}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Product Info */}
          <div className="lg:col-span-1">
            {/* Product Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-[#1A2B22] rounded-2xl overflow-hidden shadow-sm mb-6"
            >
              <img 
                src={lot.product.image} 
                alt={lot.product.name[language]}
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <p className="text-[#F5A623] text-sm font-medium mb-1">
                  {lot.product.category[language] || lot.product.category.fr}
                </p>
                <h2 className="text-xl font-bold text-[#1A5C38] dark:text-[#2D8A56] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {lot.product.name[language] || lot.product.name.fr}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {t ? 'Variété : ' : 'Variety: '}{lot.product.variety}
                </p>
              </div>
            </motion.div>

            {/* Origin Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-[#1A2B22] rounded-2xl p-6 shadow-sm mb-6"
            >
              <h3 className="text-lg font-bold text-[#1A5C38] dark:text-[#2D8A56] mb-4 flex items-center gap-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                <MapPin size={20} />
                {t ? 'Origine' : 'Origin'}
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{t ? 'Pays' : 'Country'}</p>
                  <p className="font-medium text-gray-800 dark:text-gray-200">{lot.origin.country[language] || lot.origin.country.fr}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{t ? 'Région' : 'Region'}</p>
                  <p className="font-medium text-gray-800 dark:text-gray-200">{lot.origin.region}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{t ? 'Producteur' : 'Producer'}</p>
                  <p className="font-medium text-gray-800 dark:text-gray-200">{lot.origin.producer}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{t ? 'Date de récolte' : 'Harvest date'}</p>
                  <p className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <Calendar size={16} className="text-[#1A5C38] dark:text-[#2D8A56]" />
                    {lot.origin.harvestDate}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Quality & Certifications */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-[#1A2B22] rounded-2xl p-6 shadow-sm"
            >
              <h3 className="text-lg font-bold text-[#1A5C38] dark:text-[#2D8A56] mb-4 flex items-center gap-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                <Award size={20} />
                {t ? 'Qualité & Certifications' : 'Quality & Certifications'}
              </h3>
              
              {/* Quality Score */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 dark:text-gray-400 text-sm">{t ? 'Score qualité' : 'Quality score'}</span>
                  <span className={`font-bold ${lot.qualityScore >= 80 ? 'text-green-600' : lot.qualityScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {lot.qualityScore}/100
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${lot.qualityScore >= 80 ? 'bg-green-500' : lot.qualityScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${lot.qualityScore}%` }}
                  />
                </div>
              </div>

              {/* Certifications */}
              <div className="flex flex-wrap gap-2">
                {lot.certifications.map((cert, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-[#F8F2E8] dark:bg-[#162019] text-[#1A5C38] dark:text-[#2D8A56] rounded-full text-sm font-medium"
                  >
                    {cert}
                  </span>
                ))}
              </div>

              {/* Additional info */}
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <Package size={14} />
                    {t ? 'Conditionnement' : 'Packaging'}
                  </span>
                  <span className="text-gray-700 dark:text-gray-300">{lot.packaging[language] || lot.packaging.fr}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <Calendar size={14} />
                    {t ? 'DLC' : 'Expiry'}
                  </span>
                  <span className="text-gray-700 dark:text-gray-300">{lot.expiryDate}</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Journey Timeline */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-[#1A2B22] rounded-2xl p-6 shadow-sm"
            >
              <h3 className="text-xl font-bold text-[#1A5C38] dark:text-[#2D8A56] mb-6 flex items-center gap-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                <Truck size={24} />
                {t ? 'Parcours du produit' : 'Product Journey'}
              </h3>

              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />

                {/* Journey steps */}
                <div className="space-y-6">
                  {lot.journey.map((step, index) => {
                    const isCompleted = step.status === 'completed';
                    const isInProgress = step.status === 'in_progress';
                    const isFlagged = step.status === 'flagged';

                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + index * 0.1 }}
                        className="relative flex gap-4"
                      >
                        {/* Step indicator */}
                        <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isFlagged ? 'bg-red-100 dark:bg-red-900/30' :
                          isInProgress ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                          isCompleted ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'
                        }`}>
                          {isFlagged ? (
                            <AlertTriangle className="text-red-600" size={20} />
                          ) : isInProgress ? (
                            <RefreshCw className="text-yellow-600 animate-spin" size={20} />
                          ) : isCompleted ? (
                            <CheckCircle className="text-green-600" size={20} />
                          ) : (
                            <Clock className="text-gray-400" size={20} />
                          )}
                        </div>

                        {/* Step content */}
                        <div className={`flex-1 p-4 rounded-xl ${
                          isFlagged ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
                          isInProgress ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' :
                          'bg-gray-50 dark:bg-[#162019]'
                        }`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                {t ? `Étape ${step.step}` : `Step ${step.step}`}
                              </p>
                              <h4 className="font-semibold text-[#1A5C38] dark:text-[#2D8A56]">
                                {step.location[language] || step.location.fr}
                              </h4>
                            </div>
                            {isInProgress && (
                              <span className="px-2 py-1 bg-yellow-200 text-yellow-700 text-xs rounded-full">
                                {t ? 'En cours' : 'In progress'}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {step.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Thermometer size={14} />
                              {step.temperature}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            {/* Mock Provider Notice */}
            {isMockProvider() && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                <p className="text-blue-700 dark:text-blue-400 text-sm flex items-center gap-2">
                  <AlertTriangle size={16} />
                  {t 
                    ? 'Mode démonstration : données simulées pour illustration.'
                    : 'Demo mode: simulated data for illustration purposes.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LotDetails;
